import type { Hex, ISODate } from '../contract.js';
import { copy } from '../copy.js';
import { dateLong, dateShort, gridPos, monthFromKey, monthName, monthOf, partsOf } from '../dates.js';
import { editDoc } from '../state/derive.js';
import { isYou, memberOf, stripeOf, todayMonthKey, todayOf } from '../state/lookup.js';
import type { AppState } from '../state/types.js';
import { color, icon, palette } from '../tokens.js';
import type { PersonVals } from './person.js';
import { pfpOf, pfpOfMember } from './picture.js';
import type { Pfp } from './picture.js';

/** The centre of the island and what floats above it, ported from `hubVals`, `dayIconLook` and `stepUp` (02 §3.7–3.10; 03 §3.11). */

export interface DayIcon {
  /** The stripe colours while the circle is still the tile. */
  readonly segs: readonly string[];
  readonly ring: string;
  readonly num: string;
  readonly outline: string;
}

/** `dayIconLook`: the day circle's ring, one arc per poster with 4° clear at each end. */
export function dayIconLook(st: AppState, date: ISODate): DayIcon {
  const colours = stripeOf(st, date);
  let ring: string;
  if (!colours.length) ring = 'rgba(233, 237, 245, 0.14)';
  else {
    const seg = 360 / colours.length, g = colours.length > 1 ? 4 : 0, parts: string[] = [];
    colours.forEach((c, i) => {
      const a = i * seg;
      parts.push(`rgba(0, 0, 0, 0) ${a}deg ${a + g}deg`, `${c} ${a + g}deg ${a + seg - g}deg`, `rgba(0, 0, 0, 0) ${a + seg - g}deg ${a + seg}deg`);
    });
    ring = `conic-gradient(${parts.join(', ')}), ${color.tileHover}`;
  }
  return { segs: colours, ring, num: date ? String(partsOf(date).day) : '', outline: date === todayOf(st) ? `2px solid ${color.paper}` : '2px solid transparent' };
}

export interface StepUp { readonly label: string; readonly aria: string; readonly to: 'calendar' | 'day' | 'back' }

/** `stepUp`: where the pill above the island leads, and what it says. */
export function stepUp(st: AppState): StepUp | null {
  const v = st.view;
  if (st.pe || v === 'cal' || v === 'compose') return null;
  if (v === 'day') { const month = monthName(monthOf(st.date)); return { label: month, aria: copy.hub.upCalendar(month), to: 'calendar' }; }
  if (v === 'person') return { label: dateShort(st.date), aria: copy.hub.upDay(dateLong(st.date)), to: 'day' };
  const r = st.ret || 'cal';
  if (r === 'person' && st.person) {
    const you = isYou(st, st.person), name = memberOf(st, st.person).name;
    return { label: you ? copy.hub.yourPost : copy.hub.postOf(name), aria: copy.hub.backPost(name, you, dateLong(st.date)), to: 'back' };
  }
  if (r === 'day' || r === 'person') return { label: dateShort(st.date), aria: copy.hub.backDay(dateLong(st.date)), to: 'back' };
  const month = monthName(monthFromKey(st.month));
  return { label: month, aria: copy.hub.backCalendar(month), to: 'back' };
}

export interface AddItem { readonly kind: 'media' | 'camera' | 'record' | 'text' | 'song'; readonly label: string; readonly aria: string; readonly d: string; readonly off: boolean; readonly op: number; readonly delay: string }
export interface LookVals {
  readonly pics: readonly { readonly id: string; readonly aria: string; readonly pressed: boolean; readonly ringW: string; readonly picture: Pfp }[];
  readonly upload: boolean;
  readonly colours: readonly { readonly c: Hex; readonly aria: string; readonly pressed: boolean; readonly ringW: string }[];
}

/**
 * The add menu above the +: nearest the island first. Posts: Text, Photo or video. Profiles add Song on top (04 §11).
 * On a touch device Camera (a photo) and, where videos are taken, Record video sit above Photo or video
 * (owner, 4 October 2026).
 */
export function addItems(st: AppState, camera = false): readonly AddItem[] | null {
  const doc = editDoc(st);
  if (!doc || st.tray !== 'add') return null;
  const items: Omit<AddItem, 'delay'>[] = [];
  if (doc.kind === 'profile' && st.caps.songs) {
    const taken = doc.items.some((item) => item.t === 'song');
    items.push({ kind: 'song', label: copy.hub.song, aria: taken ? copy.hub.songTaken : copy.hub.songAria, d: icon.song, off: taken, op: taken ? 0.4 : 1 });
  }
  items.push({ kind: 'text', label: copy.hub.text, aria: copy.hub.textAria, d: icon.text, off: false, op: 1 });
  if (camera) items.push({ kind: 'camera', label: copy.hub.camera, aria: copy.hub.cameraAria, d: icon.camera, off: false, op: 1 });
  if (camera && st.caps.video) items.push({ kind: 'record', label: copy.hub.record, aria: copy.hub.recordAria, d: icon.record, off: false, op: 1 });
  items.push({ kind: 'media', label: copy.hub.media, aria: copy.hub.mediaAria, d: icon.media, off: false, op: 1 });
  return items.map((item, i) => ({ ...item, delay: `${(items.length - 1 - i) * 45}ms` }));
}

/** The Look tray: preset pictures, upload, colours, as far as the backend allows each (06 §4). */
export function lookVals(st: AppState): LookVals | null {
  const pe = st.pe;
  if (!pe || st.view !== 'profile' || st.tray !== 'look') return null;
  return {
    pics: st.caps.presetPictures ? st.presets.map((preset) => {
      const on = pe.pic === preset.id;
      return { id: preset.id, aria: copy.hub.picture(preset.name), pressed: on, ringW: on ? '2px' : '0px', picture: pfpOf(st, pe.color, preset.id) };
    }) : [],
    upload: st.caps.pictureUpload,
    colours: st.caps.changeColour ? palette.map((c, i) => {
      const on = pe.color === c;
      return { c, aria: copy.hub.colour(i + 1, on), pressed: on, ringW: on ? '2px' : '0px' };
    }) : [],
  };
}

export interface HubVals {
  readonly showDay: boolean;
  readonly d: DayIcon & { readonly r: string; readonly tileOp: number; readonly circOp: number };
  readonly dOp: number; readonly dayTf: string; readonly dayTr: string;
  readonly showArrows: boolean;
  readonly arOp: number; readonly arTr: string; readonly arOff: boolean; readonly prevAria: string; readonly nextAria: string;
  /** The centre picture: the author of the open post, or the friend whose profile is open. */
  readonly showPerson: boolean;
  readonly picture: Pfp;
  readonly pTf: string; readonly pTr: string; readonly pR: string;
  readonly wob: '' | 'wobA' | 'wobB'; readonly wobDelay: string;
  readonly pOp: number; readonly pAria: string; readonly pOff: boolean; readonly inert: boolean;
  readonly up: (StepUp & { readonly op: number }) | null;
  /**
   * Home inside the island on profiles (owner decision of 2 October 2026, replacing the solo Home at
   * (956, 150)): in the centre on your own profile, left of the picture on a friend's.
   */
  readonly home: { readonly x: number; readonly aria: string; readonly op: number } | null;
  readonly add: readonly AddItem[] | null;
  readonly look: LookVals | null;
}

export function hubVals(st: AppState, pv: PersonVals, device: { readonly camera?: boolean } = {}): HubVals {
  const cd = st.cd, pp = st.pp, g = st.date ? gridPos(st.date) : { x: 700, y: 816 }, lv = st.lv;
  const dPre = cd === 'in-pre' || cd === 'out', inPerson = st.view === 'person', pShown = inPerson && pp !== 'in-pre' && pp !== 'out';
  const onProfile = st.view === 'profile', own = isYou(st, st.profileOf), profOther = onProfile && !own, pm = memberOf(st, st.profileOf);
  const look = dayIconLook(st, st.date), up = stepUp(st);
  return {
    showDay: st.view === 'day' || inPerson,
    d: { ...look, r: dPre ? '3px' : '50%', tileOp: dPre ? 1 : 0, circOp: dPre ? 0 : 1 },
    dOp: lv || pShown ? 0 : 1,
    dayTf: dPre ? `translate(${g.x - 700}px, ${g.y - 816}px) scale(1.7)` : pShown ? 'translate(8px, 8px) scale(0.6)' : 'none',
    dayTr: cd === 'in' ? 'transform 680ms cubic-bezier(.2,.8,.2,1), border-radius 520ms ease 100ms, opacity 200ms ease'
      : cd === 'out' ? 'transform 560ms cubic-bezier(.4,0,.2,1), border-radius 420ms ease, opacity 200ms ease'
        : pp === 'in' || pp === 'out' ? 'transform 420ms cubic-bezier(.2,.8,.2,1), opacity 260ms ease' : 'transform 220ms ease, opacity 200ms ease',
    showArrows: pv.show,
    arOp: lv ? 0 : pv.arOp, arTr: pv.arTr, arOff: pv.arOff || lv, prevAria: pv.prevAria, nextAria: pv.nextAria,
    showPerson: pv.show || profOther,
    picture: profOther ? pfpOfMember(st, pm) : pv.picture,
    pTf: profOther ? 'none' : pv.pTf, pTr: profOther ? 'none' : pv.pTr, pR: profOther ? '50%' : pv.pR,
    wob: profOther ? '' : pv.wob, wobDelay: pv.wobDelay, pOp: lv && !st.lvKeep ? 0 : 1,
    pAria: profOther ? copy.hub.profileOf(pm.name) : pv.pAria, pOff: profOther || lv, inert: profOther,
    up: up ? { ...up, op: lv || cd === 'in-pre' || cd === 'out-pre' || cd === 'out' || st.pfx === 'in-pre' ? 0 : 1 } : null,
    home: onProfile && !st.pe
      ? { x: own ? 698 : 648, aria: copy.home.back(monthName(monthFromKey(todayMonthKey(st)))), op: lv || st.pfx === 'in-pre' ? 0 : 1 }
      : null,
    add: addItems(st, device.camera),
    look: lookVals(st),
  };
}
